import React, { useContext, useState } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import BarChartIcon from "@material-ui/icons/BarChart";
import PersonIcon from "@material-ui/icons/Person";
import * as Dati from "src/Dati";
import * as Glob from "src/Globale";
import { Trad } from "src/components";
import { _NumberField } from "../../form";
import { AlertDialogContext } from "../../providers/AlertDialogProvider";

interface ICoInsuranceProps {
    Name: string;
    ProductBinderJSON: Glob.DatiJSON;
    ValueJSON: Glob.DatiJSON;
    OnChange: (e: React.FormEvent<any> | string, val?: unknown) => void;
}

const useStyles = makeStyles(({ shape, spacing, typography }: Theme) => ({
    icon: {
        marginRight: spacing(2)
    },
    title: {
        flexGrow: 1
    },
    toolbar: {
        borderRadius: shape.borderRadius,
        marginTop: spacing(4),
        marginBottom: spacing(2)
    }
}));

export const _CoInsurance: React.FC<ICoInsuranceProps> = ({ Name, ValueJSON, ProductBinderJSON, OnChange }) => {
    const classes = useStyles();
    const { ShowDialog } = useContext(AlertDialogContext);

    const [outstandingShare, setOutstandingShare] = useState(100);
    const value = ValueJSON ? JSON.parse(ValueJSON) : [];

    const validateLeadingInsurer = () => {
        let errorMessage: string = "";

        productBinder.forEach(item => {
            if (item.CoAssicurazioneSoloDelegataria && !item.Delegataria && Number(item.Quota) != 0) {
                errorMessage += Trad.TradParam("La compagnia {1-S} richiede di essere delegataria", [item.DenominazioneCompagnia]);
            }
        });

        if (errorMessage) {
            ShowDialog("WARNING", errorMessage, "warning");
        }
    };

    const onChangeShare = (val: string, index: number) => {
        let updatedOutstandingShare: number;
        const oldQuota: number = Number(productBinder[index].Quota);

        productBinder[index].Quota = Number(val);
        updatedOutstandingShare = calculateOutstandingShare();

        if (updatedOutstandingShare < 0) {
            ShowDialog("WARNING", Trad.TradParam("Impossibile impostare la quota di {1-R} perché supererebbe la soglia del 100% totale", [val]), "warning");

            productBinder[index].Quota = oldQuota < productBinder[index].CoAssicurazioneMinima ? 0.0 : oldQuota;
            updatedOutstandingShare = calculateOutstandingShare();
        }

        controlDelegataria();
        setOutstandingShare(updatedOutstandingShare);
        updateValue();
    };

    const onChangeDelegataria = (index: number) => {
        productBinder.forEach((item, i) => {
            item.Delegataria = i == index;
        });

        controlDelegataria();
        validateLeadingInsurer();
        updateValue();
    };

    const calculateOutstandingShare = (): number => {
        let val = 100;

        productBinder.forEach(item => {
            val = val - (item.Quota || 0);
        });

        return val;
    };

    const controlDelegataria = () => {
        let DeleQuotaMassima = -1;
        let QuotaMassima = 0;
        let DeleCorrente = -1;
        let QuotaDeleCorrente = 0;

        productBinder.forEach((item, index) => {
            if (QuotaMassima <= item.Quota) {
                DeleQuotaMassima = index;
                QuotaMassima = item.Quota;
            }
            if (item.Delegataria) {
                DeleCorrente = index;
                QuotaDeleCorrente = item.Quota || 0;
            }
        });

        if (DeleCorrente == -1) {
            if (DeleQuotaMassima >= 0) {
                productBinder[DeleQuotaMassima].Delegataria = true;
            }
        } else if (DeleCorrente != DeleQuotaMassima && QuotaDeleCorrente != QuotaMassima) {
            productBinder[DeleCorrente].Delegataria = false;
            productBinder[DeleQuotaMassima].Delegataria = true;
        }
    };

    const updateValue = () => {
        const newValue: Dati.CDatoCoAssicurazione[] = [];

        productBinder.forEach(item => {
            if (item.Quota > 0) {
                const data = new Dati.CDatoCoAssicurazione();
                data.ID_Anag = item.ID_AnagCompagnia;
                data.Delegataria = item.Delegataria;
                data.Quota = item.Quota;
                data.ID_Binder = item.ID_Binder;
                data.ID_BinderCompagnia = item.ID_BinderCompagnia;
                newValue.push(data);
            }
        });

        OnChange(Name, JSON.stringify(newValue));
    };

    const getProductBinderValue = (): Dati.CDatoBinderProdottoCoAssicurazione[] => {
        const structura = ProductBinderJSON ? JSON.parse(ProductBinderJSON) : [];

        structura.forEach((structuraItem: Dati.CDatoBinderProdottoCoAssicurazione) => {
            structuraItem.Delegataria = false;
            structuraItem.Quota = 0;

            value.forEach((valueItem: any) => {
                if (structuraItem.ID_AnagCompagnia == valueItem.ID_Anag) {
                    structuraItem.Quota = valueItem.Quota;
                    structuraItem.Delegataria = valueItem.Delegataria;
                    structuraItem.ID_BinderCompagnia = valueItem.ID_BinderCompagnia;
                }
            });
        });

        return structura;
    };

    const productBinder: Dati.CDatoBinderProdottoCoAssicurazione[] = getProductBinderValue();

    return (
        <>
            <Toolbar variant="dense" className={`${classes.toolbar} success`}>
                <PersonIcon className={classes.icon} />
                <Typography variant="subtitle1" color="inherit" className={classes.title}>
                    {Trad.Trad("Assicuratori")}
                </Typography>
                <BarChartIcon fontSize="small" />
                <Box mr={1}>
                    <Typography variant="body2" color="inherit">
                        {`${Trad.Trad("Quota rimanente")}: ${Trad.TradParam("{1-P}", [outstandingShare])}`}
                    </Typography>
                </Box>
            </Toolbar>
            <Table size="small" aria-label="co-insurance table">
                <TableHead>
                    <TableRow>
                        <TableCell>{Trad.Trad("Binder")}</TableCell>
                        <TableCell align="right">{Trad.Trad("Denominazione")}</TableCell>
                        <TableCell align="right">{Trad.Trad("Data effetto")}</TableCell>
                        <TableCell align="right">{Trad.Trad("Data fine")}</TableCell>
                        <TableCell align="right">{Trad.Trad("Limite per contraente")}</TableCell>
                        <TableCell align="right">{Trad.Trad("Premio minimo")}</TableCell>
                        <TableCell align="right">{Trad.Trad("CoAssicurazione minima")}</TableCell>
                        <TableCell align="right">{Trad.Trad("% assicurata")}</TableCell>
                        <TableCell align="center">{Trad.Trad("Delegataria")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {productBinder.map((item, index) => (
                        <TableRow key={item.ID_Binder}>
                            <TableCell>
                                <a href={`/binder/${item.ID_Binder}`}>{item.CodiceBinder}</a>
                            </TableCell>
                            <TableCell align="right">{item.DenominazioneCompagnia}</TableCell>
                            <TableCell align="right">{Trad.TradParam("{1-D}", [item.DataEffetto])}</TableCell>
                            <TableCell align="right">{Trad.TradParam("{1-D}", [item.DataFine])}</TableCell>
                            <TableCell align="right">{Trad.TradParam("{1-I}", [item.LimitePerSingoloContraente])}</TableCell>
                            <TableCell align="right">{Trad.TradParam("{1-I}", [item.PremioMinimo])}</TableCell>
                            <TableCell align="right">{Trad.TradParam("CoAssMinimaSoloDelegataria", [item.CoAssicurazioneMinima, item.CoAssicurazioneSoloDelegataria ? " *" : ""])}</TableCell>
                            <TableCell align="right">
                                <_NumberField
                                    Id="Quota"
                                    Label=""
                                    Value={item.Quota}
                                    Min={item.CoAssicurazioneMinima}
                                    Max={100}
                                    NeutralVal={0}
                                    Adornment=""
                                    Disabled={productBinder.length <= 1}
                                    OnChange={(e, val) => onChangeShare(val, index)}
                                />
                            </TableCell>
                            <TableCell align="center">
                                <Checkbox checked={item.Delegataria} onChange={e => onChangeDelegataria(index)} name="Delegataria" />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
};



// WEBPACK FOOTER //
// ./src/inar2/components/Bond/CoInsurance.tsx